import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import NotificationBell from '../components/NotificationBell'

type Theme = 'light' | 'dark' | 'system'

function applyTheme(theme: Theme) {
  const root = document.documentElement
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)
}

export default function AppLayout() {
  const { t, i18n } = useTranslation()
  const { user, logout } = useAuth()
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem('isg.theme') as Theme) || 'system')
  const [open, setOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('isg.sidebar') === 'collapsed')
  useEffect(() => { localStorage.setItem('isg.sidebar', collapsed ? 'collapsed' : 'open') }, [collapsed])
  const [q, setQ] = useState('')
  const navigate = useNavigate()
  const roles = user?.roles.map((r) => r.role) ?? []
  const isSuper = roles.includes('SuperAdmin')
  const isAdmin = isSuper || roles.includes('TenantAdmin')
  const isSupplierOnly = roles.length > 0 && roles.every((r) => r === 'Supplier')

  useEffect(() => {
    applyTheme(theme)
    localStorage.setItem('isg.theme', theme)
  }, [theme])

  const cycleTheme = () => setTheme((x) => (x === 'system' ? 'dark' : x === 'dark' ? 'light' : 'system'))
  const toggleLang = () => {
    const next = i18n.language === 'tr' ? 'en' : 'tr'
    void i18n.changeLanguage(next)
    localStorage.setItem('isg.lang', next)
  }

  const nav = [
    { to: '/', label: t('nav.dashboard'), end: true },
    { to: '/reports', label: t('nav.reports') },
    ...(isSupplierOnly ? [] : [{ to: '/reports/new', label: t('nav.newReport') }, { to: '/reports/free', label: t('nav.freeReport') }, { to: '/planning', label: t('nav.planning') }]),
    { to: '/actions', label: t('nav.actions') },
    ...(isSupplierOnly ? [] : [{ to: '/analytics', label: t('nav.analytics') }, { to: '/companies', label: t('nav.companies') }, { to: '/imports', label: t('nav.import') }, { to: '/archive', label: t('nav.archive') }, { to: '/templates', label: t('nav.templates') }]),
    ...(isAdmin ? [{ to: '/users', label: t('nav.users') }, { to: '/audit-log', label: t('nav.auditLog') }] : []),
    ...(isSuper ? [{ to: '/tenants', label: t('nav.tenants') }, { to: '/deleted-reports', label: t('nav.deletedReports') }] : []),
  ]

  return (
    <div className={`shell ${collapsed ? 'collapsed' : ''}`}>
      <button className="ghost sidebar-toggle" onClick={() => setCollapsed((c) => !c)} title={collapsed ? t('nav.expandMenu') : t('nav.collapseMenu')} aria-label={collapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}>{collapsed ? '»' : '«'}</button>
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="brand">
          <strong>{t('app.name')}</strong>
          {/* A user assigned to one company sees that company; tenant-wide accounts see the tenant. */}
          <span className="muted small" title={user?.companyName && user.tenantName ? user.tenantName : undefined}>{user?.companyName ?? user?.tenantName ?? t('roles.platform')}</span>
        </div>
        <form className="sidebar-search" onSubmit={(e) => { e.preventDefault(); if (q.trim()) { navigate(`/search?q=${encodeURIComponent(q.trim())}`); setOpen(false) } }}>
          <input placeholder={t('nav.search')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('nav.search')} />
        </form>
        <nav>
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? 'active' : '')} onClick={() => setOpen(false)}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="row between"><div className="small">{user?.fullName}</div><NotificationBell /></div>
          <div className="muted small">{user?.roles.map((r) => t(`roles.${r.role}`)).join(', ')}</div>
          <div className="row" style={{ marginTop: 8, gap: 6 }}>
            <button className="ghost" onClick={cycleTheme} title={t('theme.title')}>{theme === 'dark' ? '🌙' : theme === 'light' ? '☀️' : '🖥️'}</button>
            <button className="ghost" onClick={toggleLang}>{i18n.language === 'tr' ? 'EN' : 'TR'}</button>
            <button className="ghost" onClick={() => void logout()}>{t('auth.logout')}</button>
          </div>
        </div>
      </aside>
      <div className="content">
        <header className="topbar mobile-only">
          <button className="ghost" onClick={() => setOpen((o) => !o)} aria-label="menu">☰</button>
          <strong>{t('app.name')}</strong>
        </header>
        <main>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
